import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useFormikContext } from "formik";
import { ClaimModalFormValues } from "@/features/my-order/features/order-history/features/claim/components/OrderHistoryClaimModal";
import {
  Box,
  Flex,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../../../public/icons-v1_1";
import NoResultSimple from "@/shared/components/no-result-simple";
import { highlightPartialText } from "@/utils/highlightSearch";
import { Fragment } from "react";

interface Props {
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  filteredOptions: any[];
}

export default function OrderHistoryClaimModalSelectQuantityStep({
  searchTerm,
  setSearchTerm,
  filteredOptions,
}: Props) {
  const { t, tTitle, tSentence, isKor } = useI18nHelper();
  const { values, setFieldValue } = useFormikContext<ClaimModalFormValues>();

  const handleQuantityChange = (index: number, delta: number, max: number) => {
    const currentItem = values.items[index];
    const currentQuantity = currentItem?.quantity || 0;
    const nextQuantity = Math.max(0, Math.min(max, currentQuantity + delta));

    if (nextQuantity > currentQuantity) {
      // Add details
      const newDetails = [
        ...currentItem.details,
        ...Array.from({ length: nextQuantity - currentQuantity }, () => ({
          reasonCategory: "DAMAGED" as const,
          reasonDetail: "",
          images: [],
          unoFileIds: [],
        })),
      ];
      setFieldValue(`items.${index}.details`, newDetails);
    } else if (nextQuantity < currentQuantity) {
      // Remove details
      const newDetails = currentItem.details.slice(0, nextQuantity);
      setFieldValue(`items.${index}.details`, newDetails);
    }

    setFieldValue(`items.${index}.quantity`, nextQuantity);
  };

  return (
    <Flex flexDirection="column" gap="16px">
      <Text textStyle="body-sm-regular" color="#6F6F6C">
        {tSentence("order_history:claim.claim_selection_description")}
      </Text>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <CustomIcon src="search" width={16} height={16} color="#8F8F8C" />
        </InputLeftElement>
        <Input
          placeholder={t("order_history:claim.search_sku_placeholder")}
          borderRadius="4px"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <InputRightElement
            onClick={() => setSearchTerm("")}
            cursor={"pointer"}
          >
            <CustomIcon
              src="x-circle-solid"
              width={20}
              height={20}
              color="#8F8F8C"
            />
          </InputRightElement>
        )}
      </InputGroup>

      <Flex
        height="400px"
        overflowY="auto"
        border="1px solid #D4D4D1"
        borderRadius="8px"
        padding="12px"
        flexDirection="column"
        gap={"8px"}
      >
        {filteredOptions.length === 0 && (
          <NoResultSimple
            size={"28px"}
            text={tSentence("common:no_result.simple_description")}
          />
        )}
        {filteredOptions.map((item, index) => {
          const { colorName, category, madeIn } = item;
          const { size, orderSheetProductOptionId, claimAvailableQuantity } =
            item.option;
          const detailList = [size, category, colorName, madeIn].filter(
            Boolean
          );

          const itemIndex = values.items.findIndex(
            (val) => val.orderSheetProductOptionId === orderSheetProductOptionId
          );
          const quantity = values.items[itemIndex]?.quantity || 0;
          const isMinusDisabled = quantity <= 0;
          const isPlusDisabled = quantity >= claimAvailableQuantity;

          return (
            <Box key={`${orderSheetProductOptionId}-${index}`}>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex gap="12px" alignItems="center">
                  <Box
                    borderRadius="4px"
                    overflow="hidden"
                    boxShadow={"0 0 0 0.5px #D4D4D1"}
                  >
                    <Flex
                      width="100%"
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Image
                        src={item.imageUrl}
                        alt={"representative image"}
                        fallbackSrc={"/icons-v1_1/null.png"}
                        width={"68px"}
                        height={"68px"}
                      />
                    </Flex>
                  </Box>
                  <Flex flexDirection={"column"} gap={"2px"}>
                    <Text textStyle="body-sm-medium" color="#444444">
                      {item.brandName}
                    </Text>
                    <Text textStyle="body-sm-bold" color="#444444">
                      {highlightPartialText(item.sku, [searchTerm])}
                    </Text>
                    <Flex alignItems={"center"}>
                      {detailList.map((detail, index) => (
                        <Fragment key={index}>
                          <Text
                            textStyle="caption-medium"
                            color="#6F6F6C"
                            maxWidth={"55px"}
                            noOfLines={1}
                          >
                            {detail}
                          </Text>
                          {index !== detailList.length - 1 && (
                            <CustomIcon
                              src={"dot"}
                              width={16}
                              height={16}
                              color={"#D4D4D1"}
                            />
                          )}
                        </Fragment>
                      ))}
                    </Flex>
                  </Flex>
                </Flex>
                <Flex flexDirection="column" alignItems="center" gap="8px">
                  <Text textStyle="caption-medium" color="#6F6F6C">
                    {tTitle("order_history:claim.request_available")} :{" "}
                    {claimAvailableQuantity}
                    {isKor ? "개" : ""}
                  </Text>
                  <Flex
                    alignItems="center"
                    gap="8px"
                    boxShadow={"0 0 0 0.5px #8F8F8C"}
                    borderRadius="4px"
                    overflow="hidden"
                  >
                    <IconButton
                      aria-label="minus"
                      icon={
                        <CustomIcon
                          src="minus"
                          width={24}
                          height={24}
                          color={isMinusDisabled ? "#C0C0C0" : "#8F8F8C"}
                        />
                      }
                      variant="ghost"
                      padding={"2px"}
                      size={"28px"}
                      disabled={isMinusDisabled}
                      boxShadow={"0.5px 0 0 0 #8F8F8C !important"}
                      borderRadius={"0px"}
                      _hover={{
                        background: isMinusDisabled ? "#EFEFED" : "",
                      }}
                      backgroundColor={isMinusDisabled ? "#EFEFED" : "#FCFCFB"}
                      onClick={() =>
                        handleQuantityChange(
                          itemIndex,
                          -1,
                          claimAvailableQuantity
                        )
                      }
                    />
                    <Text
                      width="20px"
                      textAlign="center"
                      textStyle="body-sm-bold"
                    >
                      {quantity}
                    </Text>
                    <IconButton
                      aria-label="plus"
                      icon={
                        <CustomIcon
                          src="plus"
                          width={24}
                          height={24}
                          color={isPlusDisabled ? "#C0C0C0" : "#8F8F8C"}
                        />
                      }
                      variant="ghost"
                      padding={"2px"}
                      boxShadow={"-0.5px 0 0 0 #8F8F8C !important"}
                      size={"28px"}
                      borderRadius={"0px"}
                      _hover={{
                        background: isPlusDisabled ? "#EFEFED" : "",
                      }}
                      backgroundColor={isPlusDisabled ? "#EFEFED" : "#FCFCFB"}
                      disabled={isPlusDisabled}
                      onClick={() =>
                        handleQuantityChange(
                          itemIndex,
                          1,
                          claimAvailableQuantity
                        )
                      }
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
}
